import type { Track, Vec2 } from '../sim/track';
import type { CarInput, CarState } from '../sim/types';
import { neutralInput } from '../sim/types';
import { mulberry32, type Prng } from '../sim/prng';
import { lookAheadPoint, steerToward } from './Steering';
import { targetSpeed } from './SpeedControl';
import type { AiProfile } from './profiles';

/** Lissage du décalage d'évitement (anti-jitter) : l'offset rejoint sa cible à ce taux/pas. */
const AVOID_BLEND = 0.15;
/** Marge (px) gardée par rapport au mur quand on décale la cible d'évitement. */
const EDGE_MARGIN = 14;
/** Au-delà de cette fraction de la demi-largeur, on ramène activement vers le centre. */
const EDGE_PUSH_THRESHOLD = 0.62;
/** Gain de la correction de bord (px de décalage par px de dépassement). */
const EDGE_PUSH_GAIN = 0.9;
/** Durée (pas) d'une faute de conduite. */
const MISTAKE_DURATION = 16;
/** Amplitude max du braquage parasite d'une faute. */
const MISTAKE_STEER = 0.3;

/**
 * Pilote IA d'une voiture : perception (piste + voitures voisines) → cible (look-ahead décalé
 * pour l'évitement) → CarInput. Stateful : `idx` (lookup fenêtré), l'offset d'évitement lissé,
 * un buffer de latence et un PRNG seedé pour les fautes.
 * Aucune triche : il ne fixe ni position ni vitesse, il produit un CarInput comme un humain.
 */
export class Driver {
  private idx: number;
  private target: Vec2;
  /** Décalage latéral courant (px, le long de la normale de piste), lissé pour éviter le jitter. */
  private avoidOffset = 0;
  private readonly rng: Prng;
  private mistakeTimer = 0;
  private mistakeSteer = 0;
  /** Buffer de latence de réaction : inputs idéaux en attente d'être appliqués. */
  private readonly delayQueue: CarInput[] = [];

  constructor(
    private readonly profile: AiProfile,
    track: Track,
    spawn: Vec2,
    seed = 1,
  ) {
    this.idx = track.nearestSampleIndex(spawn.x, spawn.y);
    this.target = { x: spawn.x, y: spawn.y };
    this.rng = mulberry32(seed);
  }

  /** Dernier point de visée (pour l'overlay racing line). */
  get lookAheadTarget(): Vec2 {
    return this.target;
  }

  computeInput(car: CarState, track: Track, others: CarState[] = []): CarInput {
    const p = this.profile;
    this.idx = track.nearestSampleIndex(car.x, car.y, this.idx);

    // Cible de base : point de la centerline à un look-ahead proportionnel à la vitesse.
    const lookAhead = clamp(p.lookAheadBase + p.lookAheadPerSpeed * car.speed, p.lookAheadBase, p.lookAheadMax);
    const la = lookAheadPoint(track, this.idx, lookAhead);

    // Évitement : décalage latéral voulu + éventuel lâcher/frein si on colle une voiture devant.
    const { offset, lift } = this.perceive(car, track, others, p);
    this.avoidOffset += (offset - this.avoidOffset) * AVOID_BLEND; // lissage anti-jitter

    // Applique le décalage le long de la normale au point de visée, clampé pour rester en piste.
    const nrm = track.samples[track.nearestSampleIndex(la.point.x, la.point.y, la.idx)];
    const maxOff = Math.max(0, track.halfWidth - EDGE_MARGIN);
    const off = clamp(this.avoidOffset, -maxOff, maxOff);
    this.target = { x: la.point.x + nrm.nx * off, y: la.point.y + nrm.ny * off };

    // Input idéal (avant latence/fautes).
    const ideal = neutralInput();
    ideal.steer = steerToward(car, this.target, p.kSteer, p.kDamp);
    const tgt = targetSpeed(track, this.idx, p);
    if (lift) ideal.brake = 1; // trop près d'une voiture plus lente devant → on lève
    else if (car.speed < tgt) ideal.throttle = 1;
    else if (car.speed > tgt * 1.06) ideal.brake = 1;
    else ideal.throttle = 0.3; // maintien

    // Difficulté sans triche : latence de réaction puis fautes occasionnelles.
    const issued = this.applyReactionDelay(ideal, p);
    this.applyMistakes(issued, p);
    return issued;
  }

  /**
   * Perception : cherche une voiture DEVANT et dans mon couloir → renvoie un décalage latéral
   * pour la contourner (côté opposé) et un flag `lift` si je la colle en étant plus rapide.
   * Ajoute une correction de bord (retour vers le centre) si je serre trop un mur.
   * Tout en coordonnées de piste (abscisse curviligne + décalage signé) → clamp facile en piste.
   */
  private perceive(
    car: CarState,
    track: Track,
    others: CarState[],
    p: AiProfile,
  ): { offset: number; lift: boolean } {
    const sSelf = track.arcLengthAt(car.x, car.y, this.idx);
    const selfLat = track.signedOffset(car.x, car.y, this.idx);
    const L = track.length;
    let push = 0;
    let lift = false;

    for (const o of others) {
      // Distance vers l'avant le long de la piste (dans [0, L)) ; on ignore ce qui est derrière.
      const ahead = (((track.arcLengthAt(o.x, o.y) - sSelf) % L) + L) % L;
      if (ahead <= 0 || ahead > p.avoidAhead) continue;
      const oLat = track.signedOffset(o.x, o.y);
      if (Math.abs(oLat - selfLat) > p.clearance) continue; // pas dans mon couloir → ignore

      const threat = (p.avoidAhead - ahead) / p.avoidAhead; // 0 (loin) → 1 (collé)
      const dodge = oLat >= selfLat ? -1 : 1; // s'écarter du côté opposé à la voiture
      push += dodge * p.avoidStrength * threat;
      if (ahead < p.clearance * 1.6 && car.speed > o.speed) lift = true; // je la rattrape → je lève
    }

    // Correction de bord : si je serre trop un mur, biais vers le centre (et borne l'évitement).
    const edge = track.halfWidth * EDGE_PUSH_THRESHOLD;
    if (Math.abs(selfLat) > edge) push -= Math.sign(selfLat) * (Math.abs(selfLat) - edge) * EDGE_PUSH_GAIN;
    return { offset: push, lift };
  }

  /** Retarde l'application des inputs de `reactionDelaySteps` pas (buffer FIFO). */
  private applyReactionDelay(input: CarInput, p: AiProfile): CarInput {
    if (p.reactionDelaySteps <= 0) return input;
    this.delayQueue.push({ ...input });
    // Tant que le buffer n'a pas la profondeur voulue, on applique l'input courant (warmup).
    if (this.delayQueue.length <= p.reactionDelaySteps) return { ...input };
    return this.delayQueue.shift()!;
  }

  /** Fautes occasionnelles (seedées) : braquage parasite + hésitation, sur quelques pas. */
  private applyMistakes(input: CarInput, p: AiProfile): void {
    if (this.mistakeTimer > 0) {
      this.mistakeTimer--;
      input.steer = clamp(input.steer + this.mistakeSteer, -1, 1);
      input.throttle *= 0.5; // hésitation
      return;
    }
    if (p.mistakeRate > 0 && this.rng() < p.mistakeRate) {
      this.mistakeTimer = MISTAKE_DURATION;
      this.mistakeSteer = (this.rng() * 2 - 1) * MISTAKE_STEER;
    }
  }
}

function clamp(v: number, a: number, b: number): number {
  return v < a ? a : v > b ? b : v;
}
